import { FaRegEdit } from "react-icons/fa";
import { HiOutlineBolt, HiOutlinePlus } from "react-icons/hi2";
import { DEFAULT_RIBBON_STYLE, DynamicRibbonFormat, IHomeTileOption } from "@/api_framework/api_modals/HomeTileConfig";
import { StatusBadge, TileAvatar, ConfirmModal, RibbonPreview } from "../components";
import { StatusSwitch } from "../controls";
import {
  primaryButtonClassName,
  tileTypeChipClassName,
  TILE_TYPE_LABEL,
  getTileVisibilityTitle,
  DYNAMIC_RIBBON_EXAMPLES,
  RIBBON_POSITION_CLASSNAME,
  sectionLabelClassName,
} from "../interface";

const SelectedTileSummary = ({
  tile,
  isListLoading,
  configCount,
  activeCount,
}: {
  tile: IHomeTileOption;
  isListLoading: boolean;
  configCount: number;
  activeCount: number;
}) => {
  const dynamicFormat = DYNAMIC_RIBBON_EXAMPLES.find((example) => example.format === tile.dynamic_ribbon_member_format);

  return (
    <div className="flex min-w-0 items-center gap-3">
      <TileAvatar name={tile.tile_name} imageUrl={tile.image_url} />
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold leading-6 text-gray-900">{tile.tile_name} Tile</h2>
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <span className={tileTypeChipClassName}>{TILE_TYPE_LABEL[tile.tile_type]}</span>
          <StatusBadge status={tile.visible ? "ACTIVE" : "INACTIVE"} title={getTileVisibilityTitle(tile.visible)} />
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
              tile.is_dynamic_enabled
                ? "border-[#193A6A]/20 bg-[#EEF5FF] text-[#193A6A]"
                : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
          >
            Dynamic {tile.is_dynamic_enabled ? "On" : "Off"}
          </span>
          {tile.is_dynamic_enabled && dynamicFormat && (
            <span
              title={`Dynamic ribbon format: ${dynamicFormat.label}`}
              className="inline-flex items-center gap-1 rounded-full border border-[#193A6A]/20 bg-white px-2 py-0.5 text-[10px] font-semibold text-[#193A6A]"
            >
              {dynamicFormat.label}
              <span className="font-medium text-slate-500">· {dynamicFormat.text}</span>
            </span>
          )}
          <span className="text-[11px] text-slate-500">
            {isListLoading ? "Loading…" : `${configCount} configured · ${activeCount} active`}
          </span>
        </div>
      </div>
    </div>
  );
};

export const ConfigListHeader = ({
  selectedTileId,
  selectedTile,
  isListLoading,
  configCount,
  activeCount,
  isUpdatingDynamicStatus,
  onRequestDynamicStatus,
  onEditDynamicRibbon,
  onAddRule,
}: {
  selectedTileId: string;
  selectedTile?: IHomeTileOption;
  isListLoading: boolean;
  configCount: number;
  activeCount: number;
  isUpdatingDynamicStatus: boolean;
  onRequestDynamicStatus: (next: boolean) => void;
  onEditDynamicRibbon: () => void;
  onAddRule: () => void;
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
    {selectedTile ? (
      <SelectedTileSummary
        tile={selectedTile}
        isListLoading={isListLoading}
        configCount={configCount}
        activeCount={activeCount}
      />
    ) : (
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold leading-6 text-gray-900">Configurations</h2>
        <p className="mt-0.5 text-xs text-gray-500">Pick a tile to manage its ribbons.</p>
      </div>
    )}

    <div className="flex shrink-0 items-center gap-3">
      {selectedTile && (
        <div className="flex h-9 items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50/80 px-3">
          <span className="text-xs font-medium text-slate-600">Dynamic Status</span>
          <StatusSwitch
            label="Dynamic Status"
            checked={selectedTile.is_dynamic_enabled}
            disabled={isUpdatingDynamicStatus}
            onRequestChange={onRequestDynamicStatus}
          />
        </div>
      )}
      {selectedTile && (
        <button
          type="button"
          onClick={onEditDynamicRibbon}
          title="Edit dynamic ribbon style"
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
        >
          Edit
          <FaRegEdit className="h-3.5 w-3.5" />
        </button>
      )}
      {selectedTileId && (
        <button type="button" onClick={onAddRule} className={`h-9 shrink-0 ${primaryButtonClassName}`}>
          <HiOutlinePlus className="h-4 w-4" />
          Add Rule
        </button>
      )}
    </div>
  </div>
);

export const DynamicStatusModal = ({
  nextStatus,
  tileName,
  isLoading,
  selectedFormat,
  onSelectFormat,
  onConfirm,
  onClose,
}: {
  nextStatus: boolean | null;
  tileName: string;
  isLoading: boolean;
  selectedFormat: DynamicRibbonFormat | null;
  onSelectFormat: (format: DynamicRibbonFormat) => void;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <ConfirmModal
    isOpen={nextStatus !== null}
    tone="primary"
    icon={<HiOutlineBolt className="h-5 w-5" />}
    title={nextStatus ? `Turn on Dynamic ribbon for ${tileName}?` : `Turn off Dynamic Ribbon for ${tileName}?`}
    description={
      nextStatus ? (
        <>
          Static ribbon rules for this tile will be disabled. Ribbon text will be determined automatically based on the
          user’s persona and applicable discounts.
          <span className={`mt-4 block ${sectionLabelClassName}`}>Select the ribbon format for membership</span>
          <span role="radiogroup" aria-label="Ribbon format" className="mt-2 grid grid-cols-3 gap-2">
            {DYNAMIC_RIBBON_EXAMPLES.map((example) => {
              const isSelected = selectedFormat === example.format;
              return (
                <button
                  key={example.format}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  disabled={isLoading}
                  onClick={() => onSelectFormat(example.format)}
                  className={`flex flex-col items-center gap-2 rounded-lg border px-2 pb-3 pt-5 text-center transition-colors disabled:cursor-not-allowed ${
                    isSelected
                      ? "border-[#193A6A] bg-[#EEF5FF] ring-1 ring-[#193A6A]"
                      : "border-slate-200 bg-slate-50 hover:border-[#193A6A]/40 hover:bg-white"
                  }`}
                >
                  <span className="relative flex h-14 w-full items-end justify-center rounded-lg border border-slate-200 bg-white pb-1.5">
                    <span className={`absolute ${RIBBON_POSITION_CLASSNAME[DEFAULT_RIBBON_STYLE.position]}`}>
                      <RibbonPreview text={example.text} />
                    </span>
                    <span className="max-w-full truncate px-1 text-[10px] font-medium text-slate-400">{tileName}</span>
                  </span>
                  <span
                    className={`text-[10px] leading-4 ${isSelected ? "font-semibold text-[#193A6A]" : "text-slate-500"}`}
                  >
                    {example.label}
                  </span>
                </button>
              );
            })}
          </span>
        </>
      ) : (
        "Dynamic ribbon logic will be disabled. The configured static ribbon rules for this tile will apply."
      )
    }
    confirmLabel={nextStatus ? "Turn On" : "Turn Off"}
    loadingLabel="Updating…"
    isLoading={isLoading}
    isConfirmDisabled={Boolean(nextStatus) && !selectedFormat}
    onConfirm={onConfirm}
    onClose={onClose}
  />
);
